import * as React from "react";
import { FunctionComponent } from "react";
import { Redirect } from "react-router-dom";

import AllApiKeysView from "components/api-keys-settings/AllApiKeysView";
import ApiKeysView from "components/api-keys-settings/ApiKeysView";
import MyApiKeysView from "components/api-keys-settings/MyApiKeysView";
import AssetView from "components/asset-management-system/AssetView";
import AssetViewsView from "components/asset-management-system/AssetViewsView";
import AssetCustomViewPage from "components/asset-management-system/custom-views/AssetCustomViewPage";
import IntegrationsView from "components/asset-management-system/integrations-view/IntegrationsView";
import AuditLogsParentView from "components/audit-log/AuditLogsParentView";
import AuditLogsView from "components/audit-log/AuditLogsView";
import DeletionReportsView from "components/audit-log/DeletionReportsView";
import ConditionalFormattingRulesView from "components/conditional-formatting-rules/ConditionalFormattingRulesView";
import { ConnectionsView } from "components/connections/ConnectionsView";
import DashboardView from "components/dashboard/DashboardView";
import CustomerEsgDashboardView from "components/dashboard/esg/CustomerEsgDashboardView";
import EsgDashboardView from "components/dashboard/esg/EsgDashboardView";
import CustomDashboard from "components/dashboard/overview/CustomDashboard";
import DownloadPageView from "components/download-page/DownloadPageView";
import FileSharingView from "components/file-sharing/FileSharingView";
import HomeView from "components/home/HomeView";
import IosProfileDownload from "components/ios-profile-download/IosProfileDownload";
import IosProfilePage from "components/ios-profile-download/IosProfilePage";
import KnowledgeBaseView from "components/knowledge-base/KnowledgeBaseView";
import AllLicensesView from "components/licenses/AllLicensesView";
import BmsConfigurationView from "components/licenses/BmsConfigurationView";
import BmsLicenseKeysView from "components/licenses/BmsLicenseKeysView";
import ContainersView from "components/licenses/containers/ContainersView";
import DeliveryHistoryView from "components/licenses/delivery-history/DeliveryHistoryView";
import EntitlementView from "components/licenses/entitlements/EntitlementView";
import AllLicenseAlertsView from "components/licenses/license-alerts/AllLicenseAlertsView";
import LicenseEventsView from "components/licenses/license-history/LicenseEventsView";
import AllLicensePoolsView from "components/licenses/licenses-pool/AllLicensePoolsView";
import LicensesView from "components/licenses/LicensesView";
import MyLicensesView from "components/licenses/MyLicensesView";
import SerialNumberKeysView from "components/licenses/SerialNumberKeysView";
import CreateNewPasswordView from "components/login/create-new-password/CreateNewPasswordView";
import LoginView from "components/login/LoginView";
import LogoutView from "components/login/LogoutView";
import AllMcsView from "components/mcs/AllMcsView";
import NewsView from "components/news/NewsView";
import ProductsView from "components/products/ProductsView";
import AllBmsReportsView from "components/reports/AllBmsReportsView";
import AllReportsView from "components/reports/AllReportsView";
import AutopilotReportsView from "components/reports/AutopilotReportsView";
import IssueReportsView from "components/reports/IssueReportsView";
import { PassportSharedView } from "components/reports/passports/PassportSharedView";
import { PassportsView } from "components/reports/passports/PassportsView";
import ReportTemplatesView from "components/reports/report-template/ReportTemplatesView";
import ReportsView from "components/reports/ReportsView";
import DeviceValueView from "components/rules/DeviceValueView";
import RulesView from "components/rules/RulesView";
import UpliftValueView from "components/rules/UpliftValueView";
import { SettingsMainView } from "components/settings/SettingsMainView";
import { GeneralSettingsView } from "components/settings/views/GeneralSettingsView";
import { MySettingsView } from "components/settings/views/my-settings/MySettingsView";
import { SharedSettingsView } from "components/settings/views/shared-settings/SharedSettingsView";
import { SingleSignOnSettingsView } from "components/settings/views/single-sign-on-settings/SingleSignOnSettingsView";
import ApiGuideView from "components/support/api-guide/ApiDocumentationView";
import SupportView from "components/support/api-guide/SupportView";
import ReportPathsView from "components/support/report-paths/ReportPathsView";
import SupportAndHelpView from "components/support/support-and-help/SupportAndHelpView";
import AllTenantsView from "components/tenants/AllTenantsView";
import TenantProfileView from "components/tenants/profile/TenantProfileView";
import AllUsersView from "components/users/AllUsersView";
import UserProfileView from "components/users/profile/UserProfileView";
import UserGroupsView from "components/users/user-groups/UserGroupsView";
import UserRolesView from "components/users/user-roles/UserRolesView";
import UsersView from "components/users/UsersView";
import AllWorkflowsView from "components/workflows/AllWorkflowsView";
import WorkflowTemplatesManagementView from "components/workflows/workflow-templates/WorkflowTemplateManagementView";
import WorkflowTemplateView from "components/workflows/workflow-templates/WorkflowTemplateView";
import WorkflowsView from "components/workflows/WorkflowsView";
import { AssetCustomView } from "domain/asset-management/assets";
import {
    AUTH_AI_ASSISTANT,
    AUTH_API_KEY_VIEW_ALL,
    AUTH_API_KEY_VIEW_OWN,
    AUTH_ASSET_CREATE,
    AUTH_ASSET_DELETE,
    AUTH_ASSET_EDIT,
    AUTH_ASSET_INTEGRATION,
    AUTH_ASSET_MANIFEST_EDIT,
    AUTH_ASSET_MANIFEST_VIEW,
    AUTH_ASSET_VIEW,
    AUTH_AUDIT_LOG_VIEW,
    AUTH_CUSTOM_ASSET_VIEW_CREATE,
    AUTH_CUSTOM_ASSET_VIEW_DELETE,
    AUTH_CUSTOM_ASSET_VIEW_EDIT,
    AUTH_CUSTOM_ASSET_VIEW_VIEW,
    AUTH_EMS_CREATE_ENTITLEMENT,
    AUTH_EMS_SYNCHRONIZE,
    AUTH_EMS_VIEW,
    AUTH_LICENSE_ALERT,
    AUTH_LICENSE_ASSIGN,
    AUTH_LICENSE_HISTORY_VIEW_ALL,
    AUTH_LICENSE_HISTORY_VIEW_OWN,
    AUTH_LICENSE_KEY_MANAGEMENT,
    AUTH_LICENSE_POOL_VIEW,
    AUTH_LICENSE_VIEW,
    AUTH_LICENSE_VIEW_OWN,
    AUTH_REPORT_TEMPLATE_VIEW,
    AUTH_ROLE_CREATE,
    AUTH_ROLE_DEFAULT,
    AUTH_ROLE_DELETE,
    AUTH_ROLE_EDIT,
    AUTH_ROLE_VIEW,
    AUTH_SSO_CONFIGURE,
    AUTH_TENANT_SETTINGS,
    AUTH_TENANT_VIEW,
    AUTH_USER_CREATE,
    AUTH_USER_DELETE,
    AUTH_USER_EDIT,
    AUTH_USER_GROUP_CREATE,
    AUTH_USER_GROUP_DELETE,
    AUTH_USER_GROUP_EDIT,
    AUTH_USER_GROUP_VIEW,
    AUTH_USER_VIEW,
    AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
    AUTH_WORKFLOW_TEMPLATE_VIEW,
    AUTH_WORKFLOW_VIEW,
} from "domain/authority";
import { CustomReportView } from "domain/reports";
import { UserDetails } from "domain/user";
import { FeatureLicenseType } from "domain/users";
import {
    FLAG_ASSET_MANAGEMENT,
    FLAG_AUDIT_LOGS,
    FLAG_BAM_INTEGRATIONS,
    FLAG_DEVICE_PASSPORT,
    FLAG_FILE_SHARING,
    FLAG_RESTRUCTURED_SETTINGS,
} from "services/feature/FeatureFlagService";
import { hasEmsId } from "services/licenses/licenseRepository";
import {
    hasAiAssistantLicenseBundle,
    hasSubTenantCookie,
    hasTenantCookie,
    isUserParentInternal,
    tenantHasFeatureLicense,
} from "services/tenants/tenantCookieService";
import { Application } from "store/application";
import { TenantDetails } from "store/tenantDetails";

import testIds from "testIds.json";

type UserValidator = (user: UserDetails | null) => boolean;

const OK_USER_VALIDATOR: UserValidator = () => true;

export class RouteDefinition {
    public path: string;
    public title: string;
    public component: FunctionComponent;
    public secured: boolean;
    public childRoutes: RouteDefinition[];
    public testId?: string;
    public featureFlag?: string;
    public featureLicenses?: FeatureLicenseType[];
    private userValidators: UserValidator[];
    private tenantValidator?: (tenant: TenantDetails | null) => boolean;
    public custom: boolean;
    public isShared: boolean | undefined;
    public advertised: boolean | undefined;
    public canHaveChildren: boolean | undefined;
    public isArchived: boolean | undefined;
    public viewUuid: string | undefined;

    constructor(
        path: string,
        title: string,
        component: FunctionComponent,
        {
            secured = true,
            childRoutes = [],
            testId = undefined,
            featureFlag = undefined,
            featureLicenses = undefined,
            userValidators = [OK_USER_VALIDATOR],
            tenantValidator = undefined,
            custom = false,
            isShared = undefined,
            advertised = undefined,
            canHaveChildren = false,
            isArchived = false,
            viewUuid = undefined,
        }: {
            secured?: boolean;
            childRoutes?: RouteDefinition[];
            testId?: string;
            featureFlag?: string;
            featureLicenses?: FeatureLicenseType[];
            userValidators?: UserValidator[];
            tenantValidator?: (tenant: TenantDetails | null) => boolean;
            custom?: boolean;
            isShared?: boolean;
            advertised?: boolean;
            canHaveChildren?: boolean;
            isArchived?: boolean;
            viewUuid?: string;
        } = {}
    ) {
        this.path = path;
        this.title = title;
        this.component = component;
        this.secured = secured;
        this.childRoutes = childRoutes;
        this.testId = testId;
        this.featureFlag = featureFlag;
        this.featureLicenses = featureLicenses;
        this.userValidators = userValidators;
        this.tenantValidator = tenantValidator;
        this.custom = custom;
        this.isShared = isShared;
        this.advertised = advertised;
        this.canHaveChildren = canHaveChildren;
        this.isArchived = isArchived;
        this.viewUuid = viewUuid;
    }

    isValidUser(user: UserDetails | null): boolean {
        for (const each of this.userValidators) {
            const valid = each(user);
            if (!valid) {
                return valid;
            }
        }
        return this.childRoutes.length === 0 || this.childRoutes.some((route) => route.isValidUser(user));
    }

    isAccessibleTo(tenant: TenantDetails | null): boolean {
        if (this.tenantValidator && !this.tenantValidator(tenant)) {
            return false;
        }
        if (!this.featureLicenses) {
            return true;
        }
        if (!tenant) {
            return false;
        }
        return this.featureLicenses.some((feature) => {
            return tenant.featureLicenses.includes(feature);
        });
    }

    getFeatureFlag(): string | undefined {
        return this.featureFlag;
    }

    containsChildRoute(route: RouteDefinition): boolean {
        for (const childRoute of this.childRoutes) {
            if (childRoute.path === route.path) {
                return true;
            }
        }
        return false;
    }

    getAncestorPathsForTargetPath(targetPath: string): string[] | undefined {
        for (const child of this.childRoutes) {
            if (child.path === targetPath) {
                return [];
            }

            const ancestorPaths = child.getAncestorPathsForTargetPath(targetPath);

            if (ancestorPaths) {
                return [child.path, ...ancestorPaths];
            }
        }

        return undefined;
    }
}

export function enableLicenseChildRoutes(bms: boolean): void {
    LICENSES_ROUTE.childRoutes = [];
    LICENSES_ROUTE.childRoutes.push(LICENSES_MY_LICENSES_ROUTE);
    LICENSES_ROUTE.childRoutes.push(LICENSES_ALL_LICENSES_ROUTE);
    LICENSES_ROUTE.childRoutes.push(LICENSES_POOL_ROUTE);
    if (!hasTenantCookie() && !hasSubTenantCookie()) {
        LICENSES_ROUTE.childRoutes.push(LICENSES_ALERT_ROUTE);
    }
    LICENSES_ROUTE.childRoutes.push(DELIVERYHISTORY_LICENSES_ROUTE);
    LICENSES_ROUTE.childRoutes.push(LICENSE_HISTORY_ROUTE);
    LICENSES_ROUTE.childRoutes.push(LICENSES_EXTRACTION_ROUTE);

    const hasConfigurationRoute = SETTINGS_ROUTE.childRoutes.includes(CONFIGURATION_BMS_LICENSES_ROUTE);
    if (bms && isUserParentInternal()) {
        if (!hasConfigurationRoute) {
            SETTINGS_ROUTE.childRoutes.push(CONFIGURATION_BMS_LICENSES_ROUTE);
        }
    } else if (hasConfigurationRoute) {
        SETTINGS_ROUTE.childRoutes = SETTINGS_ROUTE.childRoutes.filter(
            (each) => each !== CONFIGURATION_BMS_LICENSES_ROUTE
        );
    }
}

export function enableLicenseExtractionChildRoutes(bms: boolean): void {
    LICENSES_EXTRACTION_ROUTE.childRoutes = [];
    if (bms) {
        LICENSES_EXTRACTION_ROUTE.childRoutes.push(LICENSES_BMS_LICENSES_ROUTE);
    }
    LICENSES_EXTRACTION_ROUTE.childRoutes.push(ENTITLEMENT_LICENSES_ROUTE);
    LICENSES_EXTRACTION_ROUTE.childRoutes.push(CONTAINERS_LICENSES_ROUTE);
}

export function enableSerialLicenseExtractionChildRoutes(serialKey: boolean): void {
    const hasSerial = LICENSES_EXTRACTION_ROUTE.childRoutes.includes(LICENSES_SERIAL_NUMBER_ROUTE);
    if (serialKey && !hasSerial) {
        LICENSES_EXTRACTION_ROUTE.childRoutes.push(LICENSES_SERIAL_NUMBER_ROUTE);
    } else if (!serialKey && hasSerial) {
        LICENSES_EXTRACTION_ROUTE.childRoutes = LICENSES_EXTRACTION_ROUTE.childRoutes.filter(
            (each) => each != LICENSES_SERIAL_NUMBER_ROUTE
        );
    }
}

export function enableOrDisableAllApiKeys(viewAllApiKeys: boolean | undefined): void {
    API_KEYS_ROUTE.childRoutes = [];
    BAM_API_KEYS_ROUTE.childRoutes = [];
    if (viewAllApiKeys) {
        API_KEYS_ROUTE.childRoutes.push(ALL_API_KEYS_ROUTE);
        BAM_API_KEYS_ROUTE.childRoutes.push(BAM_ALL_API_KEYS_ROUTE);
    }
    API_KEYS_ROUTE.childRoutes.push(MY_API_KEYS_ROUTE);
    BAM_API_KEYS_ROUTE.childRoutes.push(BAM_MY_API_KEYS_ROUTE);
}

export function enableWorkflowTemplateChildRoutes(isRootUser: boolean): void {
    WORKFLOWS_ROUTE.childRoutes = [];
    WORKFLOWS_ROUTE.childRoutes.push(ALL_WORKFLOWS_ROUTE);
    WORKFLOWS_ROUTE.childRoutes.push(WORKFLOW_TEMPLATES_ROUTE);
    if (!hasTenantCookie() && isRootUser) {
        WORKFLOWS_ROUTE.childRoutes.push(TEMPLATE_MANAGER_ROUTE);
    }
}

export function enableBmsReportChildRoutes(bms: boolean): void {
    REPORTS_ROUTE.childRoutes = [];
    REPORTS_ROUTE.childRoutes.push(ALL_REPORTS_ROUTE);
    if (bms) {
        REPORTS_ROUTE.childRoutes.push(ALL_BMS_REPORTS_ROUTE);
    }
    REPORTS_ROUTE.childRoutes.push(ISSUE_REPORTS_ROUTE);
    REPORTS_ROUTE.childRoutes.push(AUTOPILOT_REPORTS_ROUTE);
    REPORTS_ROUTE.childRoutes.push(DEVICE_PASSPORT_ROUTE);
    REPORTS_ROUTE.childRoutes.push(REPORT_PATHS_ROUTE);
    REPORTS_ROUTE.childRoutes.push(REPORTS_TEMPLATE_ROUTE);
    REPORTS_ROUTE.childRoutes.push(REPORTS_OUR_VIEWS_ROUTE);
}

const CUSTOM_REPORT_VIEW_PATH_PREFIX = "/reports/our-views/";
const CUSTOM_ASSET_VIEW_PATH_PREFIX = "/assets/views/";
const CUSTOM_ASSET_VIEW_FOLDER_PATH_PREFIX = "/assets/views/folder/";

const removeCustomRoutesFromRoutesByPathPrefix = (routes: RouteDefinition[], pathPrefix: string) => {
    let i = routes.length;
    while (i--) {
        if (routes[i].custom && routes[i].path.startsWith(pathPrefix)) {
            routes.splice(i, 1);
        }
    }
};

export function enableCustomReportViewRoutes(customReportViews: CustomReportView[]): void {
    // Clear any existing custom routes
    removeCustomRoutes(REPORTS_OUR_VIEWS_ROUTE.childRoutes);
    removeCustomRoutesFromRoutesByPathPrefix(ROUTES, CUSTOM_REPORT_VIEW_PATH_PREFIX);
    for (const view of customReportViews) {
        const customRoute = new RouteDefinition(
            CUSTOM_REPORT_VIEW_PATH_PREFIX + view.uuid,
            view.name,
            () => AllReportsView({ viewDetails: view }),
            { custom: true, isShared: view.shared }
        );
        if (!REPORTS_OUR_VIEWS_ROUTE.containsChildRoute(customRoute)) {
            REPORTS_OUR_VIEWS_ROUTE.childRoutes.push(customRoute);
            ROUTES.unshift(customRoute);
        }
    }
}

const removeCustomRoutes = (routes: RouteDefinition[]) => {
    let i = routes.length;
    while (i--) {
        if (routes[i].custom) {
            routes.splice(i, 1);
        }
    }
};

export const CUSTOM_DASHBOARD_OVERVIEW = new RouteDefinition(
    "/dashboard/overview/custom-overview",
    "Routes.overviewDashboard",
    CustomDashboard,
    {
        featureLicenses: ["FEATURE_OVERVIEW_DASHBOARD"],
        testId: testIds.navigation.dashboard.dashboardLink,
    }
);

export const BLANCCO_IMPACT_DASHBOARD_ROUTE = new RouteDefinition(
    "/dashboard/sustainability/blancco",
    "Routes.blanccosImpact",
    EsgDashboardView,
    {
        testId: testIds.navigation.dashboard.sustainability.blanccoImpactLink,
    }
);

export const OUR_IMPACT_DASHBOARD_ROUTE = new RouteDefinition(
    "/dashboard/sustainability/own",
    "Routes.ourImpact.title",
    CustomerEsgDashboardView,
    {
        featureLicenses: ["FEATURE_SUSTAINABILITY_DASHBOARD"],
        advertised: true,
        testId: testIds.navigation.dashboard.sustainability.ownImpactLink,
    }
);

export const SUSTAINABILITY_DASHBOARD_ROUTE = new RouteDefinition(
    "/dashboard/sustainability/blancco",
    "Routes.esgDashboard",
    EsgDashboardView,
    {
        childRoutes: [BLANCCO_IMPACT_DASHBOARD_ROUTE, OUR_IMPACT_DASHBOARD_ROUTE],
        testId: testIds.navigation.dashboard.sustainability.itself,
    }
);

export function enableCustomAssetViewRoutes(assetCustomViews: AssetCustomView[]): void {
    removeCustomRoutes(ASSET_VIEWS_ROUTE.childRoutes);
    removeCustomRoutesFromRoutesByPathPrefix(ROUTES, CUSTOM_ASSET_VIEW_PATH_PREFIX);
    if (!assetCustomViews) {
        return;
    }
    const sortedViews = [...assetCustomViews].sort((a, b) => (b.isArchived ? 1 : 0) - (a.isArchived ? 1 : 0));
    for (const view of sortedViews) {
        if (!view.folder) {
            const customRoute = new RouteDefinition(
                CUSTOM_ASSET_VIEW_PATH_PREFIX + view.uuid,
                view.name,
                () => AssetCustomViewPage({ viewDetails: view }),
                { custom: true, viewUuid: view.uuid }
            );
            if (!ASSET_VIEWS_ROUTE.containsChildRoute(customRoute)) {
                ASSET_VIEWS_ROUTE.childRoutes.push(customRoute);
                ROUTES.unshift(customRoute);
            }
        } else {
            const folderRoute = new RouteDefinition(
                CUSTOM_ASSET_VIEW_FOLDER_PATH_PREFIX + view.uuid,
                view.name,
                AssetViewsView,
                { custom: true, canHaveChildren: true, isArchived: view.isArchived, viewUuid: view.uuid }
            );
            if (view.childrenViews) {
                view.childrenViews.forEach((child) => {
                    const childRoute = new RouteDefinition(
                        CUSTOM_ASSET_VIEW_PATH_PREFIX + child.uuid,
                        child.name,
                        () => AssetCustomViewPage({ viewDetails: child }),
                        { custom: true, viewUuid: child.uuid }
                    );
                    folderRoute.childRoutes.push(childRoute);
                    ROUTES.unshift(childRoute);
                });
            }
            if (!ASSET_VIEWS_ROUTE.containsChildRoute(folderRoute)) {
                ASSET_VIEWS_ROUTE.childRoutes.push(folderRoute);
            }
        }
    }
}

export const DASHBOARD_ROUTE = new RouteDefinition("/dashboard", "Routes.dashboard", DashboardView, {
    childRoutes: [CUSTOM_DASHBOARD_OVERVIEW, SUSTAINABILITY_DASHBOARD_ROUTE],
    testId: testIds.navigation.dashboard.dashboardLink,
});

export const LOGIN_ROUTE = new RouteDefinition("/login", "Routes.login", LoginView, {
    secured: false,
    testId: "login-route",
});
export const LOGOUT_ROUTE = new RouteDefinition("/logout", "Routes.logout", LogoutView, { testId: "logout-route" });
export const PROFILE_ROUTE = new RouteDefinition("/users/profile", "Routes.profile", UserProfileView, {
    testId: testIds.navigation.userMenu.profileLink,
});
export const TENANT_PROFILE_ROUTE = new RouteDefinition("/customers/profile", "Routes.profile", TenantProfileView, {
    testId: "tenant-profile-route",
});
export const CREATE_NEW_PASSWORD_ROUTE = new RouteDefinition(
    "/create-new-password",
    "Routes.createNewPassword",
    () => CreateNewPasswordView(),
    { secured: false, testId: "create-new-password-route" }
);

const LOGGED_IN_NON_ACCESS_USER_VALIDATOR: UserValidator = (user: UserDetails | null) =>
    user !== null && !hasTenantCookie();

export const REPORTS_TEMPLATE_ROUTE = new RouteDefinition(
    "/reports/erasure/templates",
    "Routes.reportTemplates",
    ReportTemplatesView,
    {
        featureLicenses: ["FEATURE_REPORT_CUSTOMIZATION"],
        userValidators: [createAuthorityUserValidator([AUTH_REPORT_TEMPLATE_VIEW])],
        testId: testIds.navigation.reports.templatesLink,
    }
);

export const ALL_REPORTS_ROUTE = new RouteDefinition("/reports/all", "Common.allReports", () => AllReportsView(), {
    testId: testIds.navigation.reports.allReportsLink,
});

export const ALL_BMS_REPORTS_ROUTE = new RouteDefinition(
    "/reports/bms",
    "Common.mobileRetailReports",
    () => AllBmsReportsView(),
    {
        featureLicenses: ["FEATURE_REPORTING"],
        testId: testIds.navigation.reports.allBmsReportsLink,
    }
);

export const ISSUE_REPORTS_ROUTE = new RouteDefinition(
    "/reports/issue-reports",
    "Common.issueReports",
    () => IssueReportsView(),
    {
        testId: testIds.navigation.reports.issueReportsLink,
    }
);

export const AUTOPILOT_REPORTS_ROUTE = new RouteDefinition(
    "/reports/autopilot-reports",
    "Common.autopilotEnrollments",
    () => AutopilotReportsView(),
    {
        testId: testIds.navigation.reports.autopilotReportsLink,
    }
);

export const DEVICE_PASSPORT_ROUTE = new RouteDefinition("/reports/passports", "Routes.devicePassport", PassportsView, {
    testId: testIds.navigation.reports.devicePassportLink,
    featureFlag: FLAG_DEVICE_PASSPORT,
});

export const PASSPORT_SHARED_ROUTE = new RouteDefinition(
    "/passports/shared",
    "Routes.devicePassport",
    PassportSharedView,
    { secured: false }
);

export const REPORTS_OUR_VIEWS_ROUTE = new RouteDefinition(
    "/reports/our-views",
    "Routes.ourViews",
    () => AllReportsView(),
    {
        featureLicenses: ["FEATURE_REPORT_CUSTOMIZATION", "FEATURE_CUSTOM_REPORT_VIEWS"],
        testId: testIds.navigation.reports.customViews.itself,
    }
);

export const ISSUE_REPORTS_REDIRECT = new RouteDefinition(
    "/reports/issueReports",
    "",
    () => React.createElement(Redirect, { to: "/reports/issue-reports" }),
    { secured: false }
);
export const AUTOPILOT_REPORTS_REDIRECT = new RouteDefinition(
    "/reports/autopilotReports",
    "",
    () => React.createElement(Redirect, { to: "/reports/autopilot-reports" }),
    { secured: false }
);
export const REPORTS_OUR_VIEWS_REDIRECT = new RouteDefinition(
    "/reports/ourViews",
    "",
    () => React.createElement(Redirect, { to: "/reports/our-views" }),
    { secured: false }
);

export const REPORTS_ROUTE = new RouteDefinition("/reports", "Routes.reports", ReportsView, {
    childRoutes: [
        ALL_REPORTS_ROUTE,
        REPORTS_OUR_VIEWS_ROUTE,
        REPORTS_TEMPLATE_ROUTE,
        ISSUE_REPORTS_ROUTE,
        AUTOPILOT_REPORTS_ROUTE,
        DEVICE_PASSPORT_ROUTE,
    ],
    testId: testIds.navigation.reports.itself,
    featureLicenses: ["FEATURE_REPORTING"],
});

export const USERS_ALL_USERS_ROUTE = new RouteDefinition("/settings/users/all", "Common.userTitle", AllUsersView, {
    userValidators: [createAuthorityUserValidator([AUTH_USER_VIEW])],
    testId: testIds.navigation.settings.users.usersLink,
});

export const BAM_USERS_ALL_USERS_ROUTE = new RouteDefinition("/assets/users/all", "Common.userTitle", AllUsersView, {
    ...USERS_ALL_USERS_ROUTE,
    featureFlag: FLAG_ASSET_MANAGEMENT,
    featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
});

export const USERS_ROLES_ROUTE = new RouteDefinition("/settings/users/roles", "UserRoles.title", UserRolesView, {
    userValidators: [
        createAuthorityUserValidator([
            AUTH_ROLE_VIEW,
            AUTH_ROLE_CREATE,
            AUTH_ROLE_DEFAULT,
            AUTH_ROLE_DELETE,
            AUTH_ROLE_EDIT,
        ]),
    ],
    featureLicenses: ["FEATURE_CUSTOM_ROLES"],
    testId: testIds.navigation.settings.users.rolesLink,
});

export const BAM_USERS_ROLES_ROUTE = new RouteDefinition("/assets/users/roles", "UserRoles.title", UserRolesView, {
    ...USERS_ROLES_ROUTE,
    featureLicenses: ["FEATURE_CUSTOM_ROLES", "FEATURE_ASSET_MANAGEMENT"],
    featureFlag: FLAG_ASSET_MANAGEMENT,
});

export const USER_GROUPS_ROUTE = new RouteDefinition("/settings/users/groups", "UserGroups.title", UserGroupsView, {
    userValidators: [
        createAuthorityUserValidator([
            AUTH_USER_GROUP_CREATE,
            AUTH_USER_GROUP_DELETE,
            AUTH_USER_GROUP_EDIT,
            AUTH_USER_GROUP_VIEW,
        ]),
    ],
    featureLicenses: ["FEATURE_USER_GROUPS"],
    testId: testIds.navigation.settings.users.groupsLink,
});

export const BAM_USER_GROUPS_ROUTE = new RouteDefinition("/assets/users/groups", "UserGroups.title", UserGroupsView, {
    ...USER_GROUPS_ROUTE,
    featureLicenses: [...USER_GROUPS_ROUTE.featureLicenses!, "FEATURE_ASSET_MANAGEMENT"],
    featureFlag: FLAG_ASSET_MANAGEMENT,
});

const createUsersViewValidator = (): UserValidator => {
    const roleValidator = createAuthorityUserValidator([
        AUTH_ROLE_VIEW,
        AUTH_ROLE_CREATE,
        AUTH_ROLE_DEFAULT,
        AUTH_ROLE_DELETE,
        AUTH_ROLE_EDIT,
    ]);
    const userValidator = createAuthorityUserValidator([
        AUTH_USER_VIEW,
        AUTH_USER_CREATE,
        AUTH_USER_DELETE,
        AUTH_USER_EDIT,
    ]);
    return (user: UserDetails | null) => {
        if (userValidator(user)) {
            return true;
        }
        if (roleValidator(user)) {
            return tenantHasFeatureLicense("FEATURE_CUSTOM_ROLES");
        }

        return false;
    };
};

export const USERS_ROUTE = new RouteDefinition("/settings/users", "Routes.users", UsersView, {
    testId: testIds.navigation.settings.users.itself,
    userValidators: [createUsersViewValidator()],
    childRoutes: [USERS_ALL_USERS_ROUTE, USER_GROUPS_ROUTE, USERS_ROLES_ROUTE],
});
export const BAM_USERS_ROUTE = new RouteDefinition("/assets/users", "Routes.users", UsersView, {
    ...USERS_ROUTE,
    childRoutes: [BAM_USERS_ALL_USERS_ROUTE, BAM_USER_GROUPS_ROUTE, BAM_USERS_ROLES_ROUTE],
    featureFlag: FLAG_ASSET_MANAGEMENT,
    featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
});

export const TENANTS_ALL_TENANTS_ROUTE = new RouteDefinition("/settings/tenants", "Common.customers", AllTenantsView, {
    userValidators: [createAuthorityUserValidator([AUTH_TENANT_VIEW])],
    featureLicenses: ["FEATURE_TENANT_MANAGEMENT"],
    testId: testIds.navigation.settings.tenantsLink,
});

export const ALL_API_KEYS_ROUTE = new RouteDefinition(
    "/connections/api-keys/all",
    "Common.allApiKeys",
    AllApiKeysView,
    {
        userValidators: [LOGGED_IN_NON_ACCESS_USER_VALIDATOR, createAuthorityUserValidator([AUTH_API_KEY_VIEW_ALL])],
        testId: testIds.navigation.connections.apiKeys.allApiKeysLink,
    }
);

export const BAM_ALL_API_KEYS_ROUTE = new RouteDefinition(
    "/assets/support/api-keys/all",
    "Common.allApiKeys",
    AllApiKeysView,
    {
        ...ALL_API_KEYS_ROUTE,
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    }
);

export const MY_API_KEYS_ROUTE = new RouteDefinition("/connections/api-keys/own", "Common.myApiKeys", MyApiKeysView, {
    userValidators: [LOGGED_IN_NON_ACCESS_USER_VALIDATOR, createAuthorityUserValidator([AUTH_API_KEY_VIEW_OWN])],
    testId: testIds.navigation.connections.apiKeys.yourApiKeysLink,
});

export const BAM_MY_API_KEYS_ROUTE = new RouteDefinition(
    "/assets/support/api-keys/own",
    "Common.myApiKeys",
    MyApiKeysView,
    {
        ...MY_API_KEYS_ROUTE,
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    }
);

export const API_KEYS_ROUTE = new RouteDefinition("/connections/api-keys", "Routes.apiKeys", ApiKeysView, {
    userValidators: [LOGGED_IN_NON_ACCESS_USER_VALIDATOR],
    testId: testIds.navigation.connections.apiKeys.itself,
    featureLicenses: ["FEATURE_PUBLIC_APIS"],
});

export const BAM_API_KEYS_ROUTE = new RouteDefinition("/assets/support/api-keys", "Routes.apiKeys", ApiKeysView, {
    ...API_KEYS_ROUTE,
    featureFlag: FLAG_ASSET_MANAGEMENT,
    featureLicenses: [...API_KEYS_ROUTE.featureLicenses!, "FEATURE_ASSET_MANAGEMENT"],
});

// BCC-4438: Redirects for old routes
export const ALL_API_KEYS_REDIRECT = new RouteDefinition(
    "/support/apiKeys/all",
    "",
    () => React.createElement(Redirect, { to: "/support/api-keys/all" }),
    { secured: false }
);

export const MY_API_KEYS_REDIRECT = new RouteDefinition(
    "/support/apiKeys/own",
    "",
    () => React.createElement(Redirect, { to: "/support/api-keys/own" }),
    { secured: false }
);
export const API_KEYS_REDIRECT = new RouteDefinition(
    "/support/apiKeys",
    "",
    () => React.createElement(Redirect, { to: "/support/api-keys" }),
    { secured: false }
);

export const API_GUIDE_ROUTE = new RouteDefinition("/connections/api-guide", "Routes.apiGuide", ApiGuideView, {
    userValidators: [createAuthorityUserValidator([AUTH_API_KEY_VIEW_ALL, AUTH_API_KEY_VIEW_OWN])],
    testId: testIds.navigation.connections.apiGuideLink,
    featureLicenses: ["FEATURE_PUBLIC_APIS"],
});

export const BAM_API_GUIDE_ROUTE = new RouteDefinition("/assets/support/api", "Routes.apiGuide", ApiGuideView, {
    ...API_GUIDE_ROUTE,
    featureLicenses: [...API_GUIDE_ROUTE.featureLicenses!, "FEATURE_ASSET_MANAGEMENT"],
    featureFlag: FLAG_ASSET_MANAGEMENT,
});

export const REPORT_PATHS_ROUTE = new RouteDefinition("/reports/report-paths", "Routes.reportPaths", ReportPathsView, {
    testId: testIds.navigation.reports.reportPathsLink,
    featureLicenses: ["FEATURE_PUBLIC_APIS", "FEATURE_REPORTING"],
});

export const BAM_REPORT_PATHS_ROUTE = new RouteDefinition(
    "/assets/support/report-paths",
    "Routes.reportPaths",
    ReportPathsView,
    {
        ...REPORT_PATHS_ROUTE,
        featureLicenses: [...REPORT_PATHS_ROUTE.featureLicenses!, "FEATURE_ASSET_MANAGEMENT"],
        featureFlag: FLAG_ASSET_MANAGEMENT,
    }
);

export const SUPPORT_AND_HELP_ROUTE = new RouteDefinition(
    "/support/support-and-help",
    "Routes.products",
    SupportAndHelpView,
    {
        testId: testIds.navigation.support.supportAndHelpLink,
    }
);

export const BAM_SUPPORT_AND_HELP_ROUTE = new RouteDefinition(
    "/assets/support/support-and-help",
    "Routes.products",
    SupportAndHelpView,
    {
        ...SUPPORT_AND_HELP_ROUTE,
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    }
);

export const NEWS_ROUTE = new RouteDefinition("/news", "Routes.news.title", NewsView, {
    userValidators: [LOGGED_IN_NON_ACCESS_USER_VALIDATOR],
    testId: testIds.navigation.support.newsLink,
});
export const BAM_NEWS_ROUTE = new RouteDefinition("/assets/news", "Routes.news.title", NewsView, {
    ...NEWS_ROUTE,
    featureFlag: FLAG_ASSET_MANAGEMENT,
    featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
});

export const KNOWLEDGE_BASE_ROUTE = new RouteDefinition("/knowledge-base", "Routes.knowledgeBase", KnowledgeBaseView, {
    userValidators: [LOGGED_IN_NON_ACCESS_USER_VALIDATOR],
    testId: testIds.navigation.support.knowledgeBaseLink,
});

export const KNOWLEDGE_BASE_REDIRECT = new RouteDefinition(
    "/knowledgeBase",
    "",
    () => React.createElement(Redirect, { to: "/knowledge-base" }),
    { secured: false }
);

export const SUPPORT_AND_HELP_REDIRECT = new RouteDefinition(
    "/support/supportAndHelp",
    "",
    () => React.createElement(Redirect, { to: "/support/support-and-help" }),
    { secured: false }
);

export const SUPPORT_ROUTE = new RouteDefinition("/support", "Routes.support", SupportView, {
    testId: testIds.navigation.support.itself,
    childRoutes: [SUPPORT_AND_HELP_ROUTE, NEWS_ROUTE, KNOWLEDGE_BASE_ROUTE],
});

export const BAM_SUPPORT_ROUTE = new RouteDefinition("/assets/support", "Routes.help", SupportView, {
    ...SUPPORT_ROUTE,
    childRoutes: [
        BAM_SUPPORT_AND_HELP_ROUTE,
        BAM_API_GUIDE_ROUTE,
        BAM_API_KEYS_ROUTE,
        BAM_REPORT_PATHS_ROUTE,
        BAM_NEWS_ROUTE,
        KNOWLEDGE_BASE_ROUTE,
    ],
    featureFlag: FLAG_ASSET_MANAGEMENT,
    featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
});

export const HOME_ROUTE = new RouteDefinition("/home", "Routes.home", HomeView, {
    testId: testIds.navigation.home.homeLink,
});

export const PRODUCTS_ROUTE = new RouteDefinition("/products", "Routes.products", ProductsView, {
    testId: testIds.navigation.products.productsLink,
    tenantValidator: () => hasAiAssistantLicenseBundle(),
    userValidators: [createAuthorityUserValidator([AUTH_AI_ASSISTANT])],
});

function createAuthorityUserValidator(authorities: string[]): UserValidator {
    return (user: UserDetails | null) => {
        if (user == null) {
            return false;
        }
        if (authorities.length === 0) {
            return true;
        }
        const userAuthorities = new Set<string>(user.role.authorities);
        for (const each of authorities) {
            if (userAuthorities.has(each)) {
                return true;
            }
        }
        return false;
    };
}

export const LICENSES_ALL_LICENSES_ROUTE = new RouteDefinition(
    "/settings/licenses/all",
    "Licenses.allLicenses",
    AllLicensesView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_LICENSE_VIEW])],
        testId: testIds.navigation.settings.licenses.allLicensesLink,
    }
);

export const LICENSES_MY_LICENSES_ROUTE = new RouteDefinition(
    "/settings/licenses/own",
    "Licenses.myLicenses",
    MyLicensesView,
    {
        userValidators: [LOGGED_IN_NON_ACCESS_USER_VALIDATOR, createAuthorityUserValidator([AUTH_LICENSE_VIEW_OWN])],
        testId: testIds.navigation.settings.licenses.ownLicensesLink,
    }
);
export const BAM_LICENSES_MY_LICENSES_ROUTE = new RouteDefinition(
    "/assets/licenses/own",
    "Licenses.myLicenses",
    MyLicensesView,
    {
        ...LICENSES_MY_LICENSES_ROUTE,
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    }
);

export const LICENSES_POOL_ROUTE = new RouteDefinition(
    "/settings/licenses/pools",
    "Licenses.allLicensesPools",
    AllLicensePoolsView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_LICENSE_POOL_VIEW])],
        featureLicenses: ["FEATURE_LICENSE_POOLS"],
        testId: testIds.navigation.settings.licenses.poolsLink,
    }
);

export const LICENSES_ALERT_ROUTE = new RouteDefinition(
    "/settings/licenses/alerts",
    "LicenseAlerts.title",
    AllLicenseAlertsView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_LICENSE_ALERT])],
        featureLicenses: ["FEATURE_LICENSE_ALERTS"],
        testId: testIds.navigation.settings.licenses.alertsLink,
    }
);

export const LICENSES_BMS_LICENSES_ROUTE = new RouteDefinition(
    "/settings/licenses/bms",
    "LicenseKeys.mobileSolutionsKeys",
    BmsLicenseKeysView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_LICENSE_KEY_MANAGEMENT])],
        testId: testIds.navigation.settings.licenses.keyGeneration.bmsLink,
    }
);

export const LICENSES_SERIAL_NUMBER_ROUTE = new RouteDefinition(
    "/settings/licenses/serial-number",
    "LicenseKeys.serialNumberKeys",
    SerialNumberKeysView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_LICENSE_KEY_MANAGEMENT])],
        testId: testIds.navigation.settings.licenses.keyGeneration.serialKeysLink,
    }
);

export const CONFIGURATION_BMS_LICENSES_ROUTE = new RouteDefinition(
    "/settings/bms-configuration",
    "Configuration.title",
    BmsConfigurationView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_TENANT_SETTINGS])],
        testId: testIds.navigation.settings.bmsConfigurationLink,
    }
);

export const ENTITLEMENT_LICENSES_ROUTE = new RouteDefinition(
    "/settings/licenses/entitlement",
    "Entitlements.title",
    EntitlementView,
    {
        userValidators: [
            createAuthorityUserValidator([AUTH_EMS_VIEW, AUTH_EMS_CREATE_ENTITLEMENT, AUTH_EMS_SYNCHRONIZE]),
            hasEmsId,
        ],
        testId: testIds.navigation.settings.licenses.keyGeneration.entitlementsLink,
    }
);

export const CONTAINERS_LICENSES_ROUTE = new RouteDefinition(
    "/settings/licenses/containers",
    "Containers.title",
    ContainersView,
    {
        userValidators: [
            createAuthorityUserValidator([AUTH_EMS_VIEW, AUTH_EMS_CREATE_ENTITLEMENT, AUTH_EMS_SYNCHRONIZE]),
        ],
        testId: testIds.navigation.settings.licenses.keyGeneration.containersLink,
    }
);

export const DELIVERYHISTORY_LICENSES_ROUTE = new RouteDefinition(
    "/settings/licenses/delivery-history",
    "DeliveryHistory.title",
    DeliveryHistoryView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_LICENSE_VIEW, AUTH_LICENSE_ASSIGN])],
        testId: testIds.navigation.settings.licenses.deliveryHistoryLink,
    }
);

export const LICENSE_HISTORY_ROUTE = new RouteDefinition(
    "/settings/licenses/history",
    "LicenseEvent.title",
    LicenseEventsView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_LICENSE_HISTORY_VIEW_OWN, AUTH_LICENSE_HISTORY_VIEW_ALL])],
        testId: testIds.navigation.settings.licenses.historyLink,
    }
);

export const LICENSES_EXTRACTION_ROUTE = new RouteDefinition(
    "/settings/licenses/key-generation",
    "Licenses.licenseKeyGeneration",
    LicensesView,
    {
        childRoutes: [ENTITLEMENT_LICENSES_ROUTE, CONTAINERS_LICENSES_ROUTE],
        testId: testIds.navigation.settings.licenses.keyGeneration.itself,
    }
);

export const LICENSES_ROUTE = new RouteDefinition("/settings/licenses", "Licenses.licenses", LicensesView, {
    testId: testIds.navigation.settings.licenses.itself,
    childRoutes: [LICENSES_ALL_LICENSES_ROUTE, CONTAINERS_LICENSES_ROUTE, LICENSES_MY_LICENSES_ROUTE],
});

export const BAM_LICENSES_ROUTE = new RouteDefinition("/assets/licenses", "Licenses.licenses", LicensesView, {
    ...LICENSES_ROUTE,
    childRoutes: [BAM_LICENSES_MY_LICENSES_ROUTE],
    featureFlag: FLAG_ASSET_MANAGEMENT,
    featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
});

export const DOWNLOAD_PAGE_ROUTE = new RouteDefinition("/download/bms/:licenseKey?", "", DownloadPageView, {
    secured: false,
    testId: "download-page-route",
});

export const IOS_PROFILE_PAGE_ROUTE = new RouteDefinition("/download/profile/:journeyId?", "", IosProfilePage, {
    secured: false,
    testId: "ios-profile-page-route",
});

export const IOS_PROFILE_DOWNLOAD_ROUTE = new RouteDefinition(
    "/download/ios/profile/:journeyId?/complete",
    "",
    IosProfileDownload,
    {
        secured: false,
        testId: "ios-profile-download-route",
    }
);

export const WORKFLOW_TEMPLATES_ROUTE = new RouteDefinition(
    "/workflows/templates",
    "Routes.workflowTemplates",
    WorkflowTemplateView,
    {
        userValidators: [
            createAuthorityUserValidator([AUTH_WORKFLOW_TEMPLATE_VIEW, AUTH_ASSET_INTEGRATION]),
            (user: UserDetails | null) => {
                if (!user) {
                    return false;
                }
                const selectedApp = localStorage.getItem("selectedApplication");
                const isBAMMode = selectedApp === Application.BAM;
                const isBlanccoUser = isUserParentInternal();
                return !isBAMMode || (isBAMMode && isBlanccoUser);
            },
        ],
        testId: testIds.navigation.workflows.templatesLink,
    }
);

export const TEMPLATE_MANAGER_ROUTE = new RouteDefinition(
    "/workflows/templates-manager",
    "Routes.workflowTemplatesManager",
    WorkflowTemplatesManagementView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW])],
        testId: testIds.navigation.workflows.templateManagerLink,
    }
);

export const ALL_WORKFLOWS_ROUTE = new RouteDefinition("/workflows/all", "Common.workflowsTitle", AllWorkflowsView, {
    userValidators: [
        createAuthorityUserValidator([AUTH_WORKFLOW_VIEW]),
        (user: UserDetails | null) => {
            if (!user) {
                return false;
            }

            const selectedApp = localStorage.getItem("selectedApplication");
            const isBAMMode = selectedApp === Application.BAM;
            const isBlanccoUser = isUserParentInternal();

            return !isBAMMode || (isBAMMode && isBlanccoUser);
        },
    ],
    testId: testIds.navigation.workflows.workflowsLink,
});

export const WORKFLOWS_ROUTE = new RouteDefinition("/workflows", "Routes.workflowsMenu.workflows", WorkflowsView, {
    childRoutes: [ALL_WORKFLOWS_ROUTE],
    testId: testIds.navigation.workflows.itself,
    featureLicenses: ["FEATURE_WORKFLOW_BDE", "FEATURE_WORKFLOW_BMDE", "FEATURE_WORKFLOW_LUN", "FEATURE_WORKFLOW_BMP"],
    advertised: true,
});

const BAM_WORKFLOW_INTERNAL_USER_VALIDATOR: UserValidator = () => isUserParentInternal();

export const BAM_ALL_WORKFLOWS_ROUTE = new RouteDefinition(
    "/assets/workflows/all",
    "Common.workflowsTitle",
    AllWorkflowsView,
    {
        ...ALL_WORKFLOWS_ROUTE,
        userValidators: [createAuthorityUserValidator([AUTH_WORKFLOW_VIEW]), BAM_WORKFLOW_INTERNAL_USER_VALIDATOR],
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    }
);

export const BAM_WORKFLOW_TEMPLATES_ROUTE = new RouteDefinition(
    "/assets/workflows/templates",
    "Routes.workflowTemplates",
    WorkflowTemplateView,
    {
        ...WORKFLOW_TEMPLATES_ROUTE,
        userValidators: [
            createAuthorityUserValidator([AUTH_WORKFLOW_TEMPLATE_VIEW]),
            BAM_WORKFLOW_INTERNAL_USER_VALIDATOR,
        ],
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
        advertised: true,
    }
);

export const BAM_WORKFLOWS_ROUTE = new RouteDefinition(
    "/assets/workflows",
    "Routes.workflowsMenu.workflows",
    WorkflowsView,
    {
        ...WORKFLOWS_ROUTE,
        userValidators: [BAM_WORKFLOW_INTERNAL_USER_VALIDATOR],
        childRoutes: [BAM_ALL_WORKFLOWS_ROUTE, BAM_WORKFLOW_TEMPLATES_ROUTE],
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
        advertised: false,
    }
);

export const ASSET_OVERVIEW_ROUTE = new RouteDefinition(
    "/assets/overview",
    "AssetManagement.assetInventory",
    AssetCustomViewPage,
    {
        userValidators: [createAuthorityUserValidator([AUTH_ASSET_VIEW])],
        testId: testIds.navigation.assets.overviewLink,
    }
);

export const ASSET_VIEWS_ROUTE = new RouteDefinition("/assets/views", "AssetManagement.customViews", AssetViewsView, {
    userValidators: [createAuthorityUserValidator([AUTH_CUSTOM_ASSET_VIEW_VIEW])],
    testId: testIds.navigation.assets.assetViews.itself,
    childRoutes: [],
});

export const RULES_CONDITIONAL_FORMATTING_ROUTE = new RouteDefinition(
    "/assets/rules/conditional-formatting",
    "Routes.conditionalFormatting",
    ConditionalFormattingRulesView,
    {
        testId: testIds.navigation.assets.rules.conditionalFormattingLink,
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    }
);

export const RULES_UPLIFT_VALUE_ROUTE = new RouteDefinition(
    "/assets/rules/uplift-value",
    "Routes.upliftValue",
    UpliftValueView,
    {
        //TODO BCC-8084 Show only with AUTH_ASSET_VALUE_MANAGEMENT after the authority is added
        testId: testIds.navigation.assets.rules.upliftValueLink,
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    }
);

export const RULES_DEVICE_VALUE_ROUTE = new RouteDefinition(
    "/assets/rules/device-value",
    "Routes.deviceValue",
    DeviceValueView,
    {
        //TODO BCC-8084 Show only with AUTH_ASSET_VALUE_MANAGEMENT after the authority is added
        testId: testIds.navigation.assets.rules.deviceValueLink,
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    }
);

export const RULES_ROUTE = new RouteDefinition("/assets/rules", "Routes.rules", RulesView, {
    testId: testIds.navigation.assets.rules.itself,
    featureFlag: FLAG_ASSET_MANAGEMENT,
    featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    childRoutes: [RULES_CONDITIONAL_FORMATTING_ROUTE, RULES_DEVICE_VALUE_ROUTE, RULES_UPLIFT_VALUE_ROUTE],
});

export const INTEGRATIONS_ROUTE = new RouteDefinition(
    "/connections/integrations",
    "Routes.integrations",
    IntegrationsView,
    {
        testId: testIds.navigation.connections.integrationsLink,
        featureFlag: FLAG_BAM_INTEGRATIONS,
        userValidators: [createAuthorityUserValidator([AUTH_ASSET_INTEGRATION])],
        featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    }
);

export const LOGS_ROUTE = new RouteDefinition("/settings/audit-logs/logs", "Routes.logs", AuditLogsView, {
    userValidators: [createAuthorityUserValidator([AUTH_AUDIT_LOG_VIEW])],
    testId: testIds.navigation.settings.auditLogs.logsLink,
});

export const DELETION_REPORTS_ROUTE = new RouteDefinition(
    "/settings/audit-logs/deletion-reports",
    "Routes.deletionReports",
    DeletionReportsView,
    {
        userValidators: [createAuthorityUserValidator([AUTH_AUDIT_LOG_VIEW])],
        testId: testIds.navigation.settings.auditLogs.deletionReportsLink,
    }
);

export const AUDIT_LOGS_ROUTE = new RouteDefinition("/settings/audit-logs", "Routes.auditLogs", AuditLogsParentView, {
    userValidators: [createAuthorityUserValidator([AUTH_AUDIT_LOG_VIEW])],
    testId: testIds.navigation.settings.auditLogs.itself,
    childRoutes: [LOGS_ROUTE, DELETION_REPORTS_ROUTE],
    featureFlag: FLAG_AUDIT_LOGS,
});

export const FILE_SHARING_ROUTE = new RouteDefinition("/settings/file-sharing", "Routes.fileSharing", FileSharingView, {
    featureFlag: FLAG_FILE_SHARING,
    featureLicenses: ["FEATURE_FILE_SHARING"],
    testId: testIds.navigation.settings.fileSharingLink,
});

export const BAM_FILE_SHARING_ROUTE = new RouteDefinition(
    "/assets/file-sharing",
    "Routes.fileSharing",
    FileSharingView,
    {
        ...FILE_SHARING_ROUTE,
        featureFlag: FLAG_ASSET_MANAGEMENT,
        featureLicenses: ["FEATURE_FILE_SHARING", "FEATURE_ASSET_MANAGEMENT"],
    }
);

export const ASSET_MANAGEMENT_ROUTE = new RouteDefinition("/assets", "Routes.assetManagement.title", AssetView, {
    testId: testIds.navigation.assets.itself,
    userValidators: [
        createAuthorityUserValidator([
            AUTH_ASSET_CREATE,
            AUTH_ASSET_DELETE,
            AUTH_ASSET_EDIT,
            AUTH_ASSET_VIEW,
            AUTH_CUSTOM_ASSET_VIEW_CREATE,
            AUTH_CUSTOM_ASSET_VIEW_DELETE,
            AUTH_CUSTOM_ASSET_VIEW_EDIT,
            AUTH_CUSTOM_ASSET_VIEW_VIEW,
            AUTH_ASSET_MANIFEST_VIEW,
            AUTH_ASSET_MANIFEST_EDIT,
        ]),
    ],
    childRoutes: [ASSET_OVERVIEW_ROUTE, RULES_ROUTE, ASSET_VIEWS_ROUTE],
    featureFlag: FLAG_ASSET_MANAGEMENT,
    featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
});

export const ASSET_MANAGEMENT_ROUTE_BMP = new RouteDefinition("/assets", "Routes.assetManagement.title", AssetView, {
    testId: testIds.navigation.assets.itself,
    userValidators: [
        createAuthorityUserValidator([
            AUTH_ASSET_CREATE,
            AUTH_ASSET_DELETE,
            AUTH_ASSET_EDIT,
            AUTH_ASSET_VIEW,
            AUTH_CUSTOM_ASSET_VIEW_CREATE,
            AUTH_CUSTOM_ASSET_VIEW_DELETE,
            AUTH_CUSTOM_ASSET_VIEW_EDIT,
            AUTH_CUSTOM_ASSET_VIEW_VIEW,
            AUTH_ASSET_MANIFEST_VIEW,
            AUTH_ASSET_MANIFEST_EDIT,
        ]),
    ],
    childRoutes: [ASSET_OVERVIEW_ROUTE, RULES_ROUTE, ASSET_VIEWS_ROUTE],
    featureFlag: FLAG_ASSET_MANAGEMENT,
    featureLicenses: ["FEATURE_ASSET_MANAGEMENT"],
    advertised: true,
});

export const ALL_MCS_ROUTE = new RouteDefinition("/connections/mcs", "Routes.mcs", AllMcsView, {
    userValidators: [(user: UserDetails | null) => user != null],
    testId: testIds.navigation.connections.onPremiseBMPsLink,
});

export const CONNECTIONS_ROUTE = new RouteDefinition("/connections", "Routes.connections", ConnectionsView, {
    testId: testIds.navigation.connections.itself,
    childRoutes: [INTEGRATIONS_ROUTE, ALL_MCS_ROUTE, API_KEYS_ROUTE, API_GUIDE_ROUTE],
});

export const MY_SETTINGS_ROUTE = new RouteDefinition("/settings/general/my-settings", "My settings", MySettingsView, {
    testId: testIds.navigation.settings.general.mySettingsLink,
    featureFlag: FLAG_RESTRUCTURED_SETTINGS,
});

export const SHARED_SETTINGS_ROUTE = new RouteDefinition(
    "/settings/general/shared-settings",
    "Routes.sharedSettings",
    SharedSettingsView,
    {
        testId: testIds.navigation.settings.general.sharedSettingsLink,
        featureFlag: FLAG_RESTRUCTURED_SETTINGS,
    }
);

export const SINGLE_SIGN_ON_SETTINGS_ROUTE = new RouteDefinition(
    "/settings/general/single-sign-on",
    "Routes.singleSignOn",
    SingleSignOnSettingsView,
    {
        featureLicenses: ["FEATURE_SSO"],
        userValidators: [createAuthorityUserValidator([AUTH_SSO_CONFIGURE])],
        testId: testIds.navigation.settings.general.singleSignOnLink,
        featureFlag: FLAG_RESTRUCTURED_SETTINGS,
    }
);

// TODO BCC-7959: Remove featureFlag property from this route and it's child routes on feature flag removal
export const GENERAL_SETTINGS_ROUTE = new RouteDefinition(
    "/settings/general",
    "Routes.generalSettings",
    GeneralSettingsView,
    {
        childRoutes: [MY_SETTINGS_ROUTE, SHARED_SETTINGS_ROUTE, SINGLE_SIGN_ON_SETTINGS_ROUTE],
        testId: testIds.navigation.settings.general.itself,
        featureFlag: FLAG_RESTRUCTURED_SETTINGS,
    }
);

export const SETTINGS_ROUTE = new RouteDefinition("/settings", "Routes.management", SettingsMainView, {
    testId: testIds.navigation.settings.itself,
    childRoutes: [
        GENERAL_SETTINGS_ROUTE,
        LICENSES_ROUTE,
        USERS_ROUTE,
        TENANTS_ALL_TENANTS_ROUTE,
        AUDIT_LOGS_ROUTE,
        FILE_SHARING_ROUTE,
    ],
});

// Note: child routes must precede parent routes. E.g. ENTITLEMENT_LICENSES_ROUTE must come before LICENSES_ROUTE.
export const ROUTES: RouteDefinition[] = [
    BAM_REPORT_PATHS_ROUTE,
    REPORT_PATHS_ROUTE,
    BAM_API_GUIDE_ROUTE,
    API_GUIDE_ROUTE,
    BAM_SUPPORT_AND_HELP_ROUTE,
    SUPPORT_AND_HELP_ROUTE,
    SUPPORT_AND_HELP_REDIRECT,
    CREATE_NEW_PASSWORD_ROUTE,
    CUSTOM_DASHBOARD_OVERVIEW,
    BLANCCO_IMPACT_DASHBOARD_ROUTE,
    OUR_IMPACT_DASHBOARD_ROUTE,
    SUSTAINABILITY_DASHBOARD_ROUTE,
    DASHBOARD_ROUTE,
    LOGIN_ROUTE,
    LOGOUT_ROUTE,
    PROFILE_ROUTE,
    TENANT_PROFILE_ROUTE,
    REPORTS_TEMPLATE_ROUTE,
    ALL_REPORTS_ROUTE,
    ALL_BMS_REPORTS_ROUTE,
    ISSUE_REPORTS_ROUTE,
    ISSUE_REPORTS_REDIRECT,
    AUTOPILOT_REPORTS_ROUTE,
    AUTOPILOT_REPORTS_REDIRECT,
    DEVICE_PASSPORT_ROUTE,
    PASSPORT_SHARED_ROUTE,
    REPORTS_ROUTE,
    REPORTS_OUR_VIEWS_ROUTE,
    REPORTS_OUR_VIEWS_REDIRECT,
    BAM_ALL_API_KEYS_ROUTE,
    BAM_MY_API_KEYS_ROUTE,
    MY_API_KEYS_ROUTE,
    MY_API_KEYS_REDIRECT,
    ALL_API_KEYS_ROUTE,
    ALL_API_KEYS_REDIRECT,
    BAM_API_KEYS_ROUTE,
    API_KEYS_ROUTE,
    API_KEYS_REDIRECT,
    BAM_SUPPORT_ROUTE,
    SUPPORT_ROUTE,
    TENANTS_ALL_TENANTS_ROUTE,
    BAM_USERS_ALL_USERS_ROUTE,
    USERS_ALL_USERS_ROUTE,
    BAM_USERS_ROLES_ROUTE,
    USERS_ROLES_ROUTE,
    BAM_USER_GROUPS_ROUTE,
    USER_GROUPS_ROUTE,
    BAM_USERS_ROUTE,
    USERS_ROUTE,
    BAM_LICENSES_MY_LICENSES_ROUTE,
    LICENSES_MY_LICENSES_ROUTE,
    LICENSES_ALL_LICENSES_ROUTE,
    LICENSES_ALERT_ROUTE,
    LICENSES_POOL_ROUTE,
    DELIVERYHISTORY_LICENSES_ROUTE,
    LICENSE_HISTORY_ROUTE,
    LICENSES_BMS_LICENSES_ROUTE,
    LICENSES_SERIAL_NUMBER_ROUTE,
    CONFIGURATION_BMS_LICENSES_ROUTE,
    ENTITLEMENT_LICENSES_ROUTE,
    CONTAINERS_LICENSES_ROUTE,
    LICENSES_EXTRACTION_ROUTE,
    BAM_LICENSES_ROUTE,
    LICENSES_ROUTE,
    DOWNLOAD_PAGE_ROUTE,
    WORKFLOW_TEMPLATES_ROUTE,
    TEMPLATE_MANAGER_ROUTE,
    BAM_ALL_WORKFLOWS_ROUTE,
    BAM_WORKFLOW_TEMPLATES_ROUTE,
    BAM_WORKFLOWS_ROUTE,
    ALL_WORKFLOWS_ROUTE,
    WORKFLOWS_ROUTE,
    IOS_PROFILE_PAGE_ROUTE,
    IOS_PROFILE_DOWNLOAD_ROUTE,
    ALL_MCS_ROUTE,
    INTEGRATIONS_ROUTE,
    RULES_CONDITIONAL_FORMATTING_ROUTE,
    RULES_UPLIFT_VALUE_ROUTE,
    RULES_DEVICE_VALUE_ROUTE,
    RULES_ROUTE,
    BAM_FILE_SHARING_ROUTE,
    BAM_NEWS_ROUTE,
    HOME_ROUTE,
    NEWS_ROUTE,
    KNOWLEDGE_BASE_ROUTE,
    KNOWLEDGE_BASE_REDIRECT,
    ASSET_OVERVIEW_ROUTE,
    ASSET_VIEWS_ROUTE,
    ASSET_MANAGEMENT_ROUTE,
    FILE_SHARING_ROUTE,
    LOGS_ROUTE,
    DELETION_REPORTS_ROUTE,
    AUDIT_LOGS_ROUTE,
    PRODUCTS_ROUTE,
    CONNECTIONS_ROUTE,
    MY_SETTINGS_ROUTE,
    SHARED_SETTINGS_ROUTE,
    SINGLE_SIGN_ON_SETTINGS_ROUTE,
    GENERAL_SETTINGS_ROUTE,
    SETTINGS_ROUTE,
];

export const detectApplicationFromPath = (pathName: string) =>
    pathName.startsWith("/assets") ? Application.BAM : Application.BMP;

export function deriveRedirectTarget(
    route: RouteDefinition,
    isRouteEnabled: (route: RouteDefinition) => boolean
): RouteDefinition {
    const target = route.childRoutes.find((childRoute) => isRouteEnabled(childRoute));

    if (target == null) {
        return LOGIN_ROUTE;
    }
    if (target.childRoutes.length === 0) {
        return target;
    }
    return deriveRedirectTarget(target, isRouteEnabled);
}
